import { FC } from 'react';
import { CareMapDrawer } from '../caremap/CareMapDrawer';
import { Redirect, Route } from 'react-router';
import { TrackPageView } from '../metrics/TrackPageView';
import { HouseholdMemberList } from '../household/HouseholdMemberList';
import { PatientDrawerView } from '../household/PatientDrawerView';
import { HouseholdMemberView } from '../household/HouseholdMemberView';
import { CaremapDrawerPatientEdit } from '../household/patient/CaremapDrawerPatientEdit';
import { HouseholdMemberEdit } from '../household/HouseholdMemberEdit';
import { CareMapNotesView } from '../notes/CareMapNotesView';
import { AddOrEditNoteUsingCareMapDrawer } from '../notes/AddOrEditNoteUsingCareMapDrawer';
import { ViewNoteUsingCareMapDrawer } from '../notes/view-note/ViewNoteUsingCareMapDrawer';
import { AppointmentsView } from '../appointments/AppointmentsView';
import { AppointmentEditorCareMapDrawer } from '../appointments/AppointmentEditorCareMapDrawer';
import { useDismissOverlays } from '../utils/useDismissOverlays';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { MedicationEditorCareMapDrawer } from '../household/patient/MedicationEditorCareMapDrawer';

export const HouseholdRouter: FC = () => {
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    const dismissOverlays = useDismissOverlays();
    return (
        <CareMapDrawer
            basePath="/caremaps/:careMapId/drawer/household"
        >
            <Route exact path="/caremaps/:careMapId/drawer/household" render={() => (
                <>
                    <TrackPageView page="Household List" />
                    <HouseholdMemberList />
                </>
            )} />

            <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId" render={(routeProps) => {
                const memberId = routeProps.match.params.memberId;
                if (memberId === 'patient') {
                    return (
                        <>
                            <TrackPageView page="Care Recipient Details" />
                            <PatientDrawerView />
                        </>
                    );
                }
                return (
                    <>
                        <TrackPageView page="Household Member Details" />
                        <HouseholdMemberView memberId={memberId} />
                    </>
                );
            }} />

            <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/edit" render={(routeProps) => {
                if (isAnonymous) {
                    const careMapId = routeProps.match.params.careMapId;
                    return <Redirect to={`/caremaps/${careMapId}`} />;
                }
                const memberId = routeProps.match.params.memberId;
                if (memberId === 'patient') {
                    return <CaremapDrawerPatientEdit />;
                }
                return <HouseholdMemberEdit memberId={memberId} />;
            }} />

            <Route exact path="/caremaps/:careMapId/drawer/household/add-member" render={(routeProps) => {
                const careMapId = routeProps.match.params.careMapId;
                const searchParams = new URLSearchParams(routeProps.location.search);
                return isAnonymous ?
                    <Redirect to={`/caremaps/${careMapId}`} /> :
                    <HouseholdMemberEdit autoSelectFromContacts={Boolean(searchParams.get('aafc'))} />;
            }} />

            <Route exact path="/caremaps/:careMapId/drawer/household/notes" render={() => {
                return (
                    <CareMapNotesView
                        titlePrefix="Household"
                        noteTarget={{section: 'household'}} />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/notes/:noteId" render={(routeProps) => {
                const noteId = routeProps.match.params.noteId;
                return (
                    <ViewNoteUsingCareMapDrawer
                        noteId={noteId}
                        defaultNoteTarget={{section: 'household'}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/notes/:noteId/edit" render={(routeProps) => {
                const noteId = routeProps.match.params.noteId;
                return (
                    <AddOrEditNoteUsingCareMapDrawer
                        noteId={noteId}
                        defaultNoteTarget={{section: 'household'}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/notes/add-note" render={(routeProps) => {
                return (
                    <AddOrEditNoteUsingCareMapDrawer
                        noteId=""
                        defaultNoteTarget={{section: 'household'}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/notes" render={(routeProps) => {
                const memberId = routeProps.match.params.memberId;
                const isPatient = memberId === 'patient';
                const titlePrefix = isPatient ? 'Care Recipient' : 'Inner Circle Member';

                return (
                    <CareMapNotesView
                        titlePrefix={titlePrefix}
                        noteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? undefined : memberId}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/notes/:noteId" render={(routeProps) => {
                const {memberId} = routeProps.match.params;
                const isPatient = memberId === 'patient';
                const {noteId} = routeProps.match.params;

                // Handle add-note case - redirect to edit route
                if (noteId === 'add-note') {
                    return (
                        <AddOrEditNoteUsingCareMapDrawer
                            noteId=""
                            defaultNoteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? undefined : memberId}}
                        />
                    );
                }

                return (
                    <ViewNoteUsingCareMapDrawer
                        noteId={noteId}
                        defaultNoteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? undefined : memberId}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/:memberId/notes/:noteId/edit" render={(routeProps) => {
                const {memberId} = routeProps.match.params;
                const isPatient = memberId === 'patient';
                const {noteId} = routeProps.match.params;

                return (
                    <AddOrEditNoteUsingCareMapDrawer
                        noteId={noteId}
                        defaultNoteTarget={{section: isPatient ? 'patient' : 'household', memberId: isPatient ? undefined : memberId}}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/patient/appointments" render={(routeProps) => {
                return <AppointmentsView />;
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/patient/appointments/:appointmentId" render={(routeProps) => {
                dismissOverlays();

                const {appointmentId} = routeProps.match.params;
                return (
                    <AppointmentEditorCareMapDrawer
                        from={'care recipient'} // tracking
                        appointmentId={appointmentId}
                    />
                );
            }} />
            <Route exact path="/caremaps/:careMapId/drawer/household/member/patient/medications/:id" render={(routeProps) => {
                const {id} = routeProps.match.params;
                return (
                    <MedicationEditorCareMapDrawer id={id} />
                );
            }} />
        </CareMapDrawer>
    );
};
